<template>
  <div class="detail-header">
    <div class="robot-selection-container">
      <button class="nav-btn prev" @click="scrollRobots('left')" :title="langMap.dashboard_scrollLeft[lang]">
        <div class="arrow-3d">◀</div>
      </button>
      
      <!-- 搜索框 -->
      <div class="search-container" :class="{ expanded: searchExpanded }">
        <button class="search-btn" @click="toggleSearch" :title="langMap.dashboard_search[lang]">
          <span class="search-icon"></span>
        </button>
        <input 
          v-if="searchExpanded"
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          class="search-input"
          :placeholder="langMap.dashboard_searchPlaceholder[lang]"
          @blur="handleSearchBlur"
        />
        <button v-if="searchQuery" class="clear-btn" @click="clearSearch" :title="langMap.dashboard_clear[lang]">
          <span class="close-icon"></span>
        </button>
      </div>
      
      <div class="robot-selector">
        <div class="selector-bg"></div>
        <div class="selector-inner" ref="selectorInner">
          <div 
            v-for="robot in filteredRobots" 
            :key="robot.id"
            class="robot-item"
            :class="{ 
              'active': selectedRobot && selectedRobot.id === robot.id,
              'online': robot.status === 'online',
              'offline': robot.status === 'offline'
            }"
            @click="switchRobot(robot)"
          >
            <div class="hexagon-bg"></div>
            <div class="robot-content">
              <div class="robot-avatar">
                <div class="avatar-ring"></div>
                <img :src="require('@/assets/images/robotCommon/robot-1.png')" alt="Robot" class="robot-icon">
                <span class="status-dot"></span>
              </div>
              <div class="robot-info">
                <span class="robot-name">{{ robot.name }}</span>
                <div class="status-line">
                  <span class="robot-status">{{ robot.status === 'online' ? langMap.dashboard_online[lang] : langMap.dashboard_offline[lang] }}</span>
                  <span class="signal-bars">
                    <span class="bar"></span>
                    <span class="bar"></span>
                    <span class="bar"></span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <button class="nav-btn next" @click="scrollRobots('right')" :title="langMap.dashboard_scrollRight[lang]">
        <div class="arrow-3d">▶</div>
      </button>
    </div>
    
    <div class="header-right" v-show="hasSelectedRobot">
      <div class="current-robot">
        <div class="hologram-effect"></div>
        <div class="robot-id">{{ selectedRobot.code || '' }}</div>
        <div class="name-status">
          <span class="value">{{ selectedRobot.name || '' }}</span>
            <span class="status-badge" :class="selectedRobot ? selectedRobot.status : ''">
            {{ selectedRobot.status === 'online' ? langMap.dashboard_online[lang] : langMap.dashboard_offline[lang] }}
          </span>
        </div>
        <!-- 模式指示器 -->
        <div class="mode-indicator" :class="getModeClass">
          <div class="indicator-badge">
            <i :class="getModeIcon"></i>
            <span class="mode-text">{{ getModeText }}</span>
          </div>
          <div class="indicator-detail">
            <span class="detail-text">{{ getDetailText }}</span>
          </div>
        </div>
      </div>
      
      <!-- 两层 Tab 导航 -->
      <div class="tab-navigation" :class="{ 'history-mode': dataSource === 'history' }">
        <!-- 第一层：数据来源 -->
        <div class="primary-tabs">
          <div 
            class="primary-tab"
            :class="{ active: dataSource === 'realtime', disabled: !isRobotOnline }"
            @click="handleDataSourceChange('realtime')"
          >
            <i class="el-icon-video-play"></i>
            <span>{{ langMap.dashboard_realtimeMonitor[lang] }}</span>
          </div>
          <div 
            class="primary-tab history-tab"
            :class="{ active: dataSource === 'history' }"
            @click="handleDataSourceChange('history')"
          >
            <i class="el-icon-time"></i>
            <span>{{ langMap.dashboard_historyQuery[lang] }}</span>
          </div>
        </div>
        
        <!-- 第二层：数据类型 -->
        <div class="secondary-tabs">
          <div 
            class="secondary-tab"
            :class="{ active: viewMode === 'status' }"
            @click="handleViewChange('status')"
          >
            <i class="el-icon-view"></i>
            <span>{{ langMap.dashboard_statusTab[lang] }}</span>
          </div>
          <div 
            class="secondary-tab"
            :class="{ active: viewMode === 'error' }"
            @click="handleViewChange('error')"
          >
            <i class="el-icon-warning-outline"></i>
            <span>{{ langMap.dashboard_errorTab[lang] }}</span>
          </div>
          <div 
            v-if="dataSource === 'history'"
            class="secondary-tab"
            :class="{ active: viewMode === 'emergency' }"
            @click="handleViewChange('emergency')"
          >
            <i class="el-icon-bell"></i>
            <span>{{ langMap.dashboard_emergencyTab[lang] }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import langMap from '@/utils/language'
import { mapState } from 'vuex';
export default {
  name: 'DetailHeader',
  props: {
    robots: {
      type: Array,
      required: true
    },
    selectedRobot: {
      type: Object,
      required: true
    },
    viewMode: {
      type: String,
      default: 'status'
    },
    dataSource: {
      type: String,
      default: 'realtime'
    }
  },
  data() {
    return {
      langMap,
      searchQuery: '',
      searchExpanded: false
    };
  },
  mounted() {
    this.scrollToSelectedRobot();
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    hasSelectedRobot() {
      return this.selectedRobot && Object.keys(this.selectedRobot).length > 0;
    },
    filteredRobots() {
      if (!this.searchQuery) return this.robots;
      
      const query = this.searchQuery.toLowerCase().trim();
      return this.robots.filter(robot => {
        const name = (robot.name || '').toLowerCase();
        const code = (robot.code || '').toLowerCase();
        return name.includes(query) || code.includes(query);
      });
    },
    isRobotOnline() {
      return this.selectedRobot.status === 'online';
    },
    getModeClass() {
      // 返回组合类名：数据来源 + 数据类型
      const sourceClass = this.dataSource === 'realtime' ? 'mode-realtime' : 'mode-history';
      const typeClass = this.viewMode === 'status' ? 'mode-status' : 'mode-error';
      return `${sourceClass} ${typeClass}`;
    },
    getModeIcon() {
      return this.dataSource === 'realtime' ? 'el-icon-video-play' : 'el-icon-time';
    },
    getModeText() {
      const prefix = this.dataSource === 'realtime'
        ? this.langMap.dashboard_realtimePrefix[this.lang]
        : this.langMap.dashboard_historyPrefix[this.lang];
      let suffixKey = 'dashboard_modeStatusSuffix';
      if (this.viewMode === 'error') suffixKey = 'dashboard_modeErrorSuffix';
      else if (this.viewMode === 'emergency') suffixKey = 'dashboard_modeEmergencySuffix';
      return prefix + this.langMap[suffixKey][this.lang];
    },
    getDetailText() {
      const lang = this.lang;
      if (this.dataSource === 'realtime' && this.viewMode === 'status') {
        return this.langMap.dashboard_realtimeStatusDetail[lang];
      } else if (this.dataSource === 'realtime' && this.viewMode === 'error') {
        return this.langMap.dashboard_realtimeErrorDetail[lang];
      } else if (this.dataSource === 'history' && this.viewMode === 'status') {
        return this.langMap.dashboard_historyStatusDetail[lang];
      } else if (this.dataSource === 'history' && this.viewMode === 'emergency') {
        return this.langMap.dashboard_historyEmergencyDetail[lang];
      } else {
        return this.langMap.dashboard_historyErrorDetail[lang];
      }
    }
  },
  watch: {
    selectedRobot() {
      console.log(3434)
      this.$nextTick(() => {
        this.scrollToSelectedRobot();
      });
    }
  },
  methods: {
    switchRobot(robot) {
      console.log(2234)
      this.$emit('switch-robot', robot)
    },
    handleViewChange(mode) {
      if (mode !== this.viewMode) {
        this.$emit('toggle-view', mode)
      }
    },
    handleDataSourceChange(source) {
      // 离线时不能切换到实时
      if (source === 'realtime' && !this.isRobotOnline) {
        return;
      }
      if (source !== this.dataSource) {
        this.$emit('toggle-data-source')
      }
    },
    scrollRobots(direction) {
      const container = this.$refs.selectorInner
      if (container) {
        const scrollAmount = 200
        if (direction === 'left') {
          container.scrollBy({ left: -scrollAmount, behavior: 'smooth' })
        } else {
          container.scrollBy({ left: scrollAmount, behavior: 'smooth' })
        }
      }
    },
    scrollToSelectedRobot() {
      if (Object.keys(this.selectedRobot).length !== 0 || !this.$refs.selectorInner) return;
      
      const container = this.$refs.selectorInner;
      const selectedItem = container.querySelector('.robot-item.active');
      
      if (selectedItem) {
        const containerRect = container.getBoundingClientRect();
        const itemRect = selectedItem.getBoundingClientRect();
        const scrollLeft = itemRect.left - containerRect.left - (containerRect.width / 2) + (itemRect.width / 2);
        
        container.scrollBy({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    },
    toggleSearch() {
      this.searchExpanded = !this.searchExpanded;
      if (this.searchExpanded) {
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
        });
      } else {
        this.searchQuery = '';
      }
    },
    clearSearch() {
      this.searchQuery = '';
      this.$refs.searchInput?.focus();
    },
    handleSearchBlur() {
      // 延迟收缩，给清除按钮时间响应
      setTimeout(() => {
        if (!this.searchQuery) {
          this.searchExpanded = false;
        }
      }, 200);
    }
  }
}
</script>

<style lang="less" scoped>
.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  position: relative;
}
  
.robot-selection-container {
    flex: 1;
    margin-right: 20px;
    position: relative;
    display: flex;
    align-items: center;
    min-width: 400px;
    // 搜索容器
    .search-container {
      display: flex;
      align-items: center;
      gap: 5px;
      margin: 0 10px;
      transition: all 0.3s ease;
      
      .search-btn {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, rgba(0, 40, 80, 0.9), rgba(0, 100, 160, 0.7));
        border: 1px solid rgba(0, 240, 255, 0.6);
        border-radius: 8px;
        color: #00f0ff;
        font-size: 18px;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 0 0 15px rgba(0, 0, 0, 0.3), inset 0 0 10px rgba(0, 240, 255, 0.2);
        position: relative;
        z-index: 5;
        
        .search-icon {
          position: relative;
          display: block;
          width: 16px;
          height: 16px;
          border: 2px solid #00f0ff;
          border-radius: 50%;
          z-index: 2;
          
          &::after {
            content: '';
            position: absolute;
            width: 2px;
            height: 8px;
            background: #00f0ff;
            bottom: -8px;
            right: -4px;
            transform: rotate(-45deg);
            border-radius: 2px;
          }
        }
        
        &:hover {
          background: linear-gradient(135deg, rgba(0, 60, 100, 0.9), rgba(0, 140, 200, 0.7));
          border-color: rgba(0, 240, 255, 0.9);
          box-shadow: 0 0 20px rgba(0, 0, 0, 0.4), inset 0 0 15px rgba(0, 240, 255, 0.4);
          transform: scale(1.05);
        }
      }
      
      .search-input {
        width: 200px;
        height: 40px;
        padding: 0 12px;
        background: linear-gradient(135deg, rgba(0, 21, 41, 0.95), rgba(0, 40, 80, 0.9));
        border: 1px solid rgba(0, 240, 255, 0.5);
        border-radius: 8px;
        color: #fff;
        font-size: 14px;
        outline: none;
        transition: all 0.3s ease;
        box-shadow: inset 0 0 10px rgba(0, 240, 255, 0.15);
        animation: slideIn 0.3s ease;
        
        &::placeholder {
          color: rgba(0, 240, 255, 0.5);
        }
        
        &:focus {
          border-color: rgba(0, 240, 255, 0.8);
          box-shadow: 0 0 15px rgba(0, 240, 255, 0.3), inset 0 0 10px rgba(0, 240, 255, 0.2);
        }
      }
      
      .clear-btn {
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(255, 45, 45, 0.2);
        border: 1px solid rgba(255, 45, 45, 0.5);
        border-radius: 6px;
        color: #FF2D2D;
        font-size: 14px;
        cursor: pointer;
        transition: all 0.2s ease;
        position: relative;
        z-index: 5;
        
        .close-icon {
          position: relative;
          display: block;
          width: 14px;
          height: 14px;
          
          &::before,
          &::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 100%;
            height: 2px;
            background: #FF2D2D;
            border-radius: 1px;
          }
          
          &::before {
            transform: translate(-50%, -50%) rotate(45deg);
          }
          
          &::after {
            transform: translate(-50%, -50%) rotate(-45deg);
          }
        }
        
        &:hover {
          background: rgba(255, 45, 45, 0.3);
          border-color: rgba(255, 45, 45, 0.8);
          transform: scale(1.1);
        }
      }
    }
    
    .nav-btn {
      width: 50px;
      height: 50px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      z-index: 10;
      background: transparent;
      border: none;
      position: relative;
      
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, 
          rgba(0, 40, 80, 0.9), 
          rgba(0, 100, 160, 0.7)
        );
        border-radius: 8px;
        transform: skewX(-10deg);
        border: 1px solid rgba(0, 240, 255, 0.6);
        box-shadow: 0 0 15px rgba(0, 0, 0, 0.3), 
                   inset 0 0 10px rgba(0, 240, 255, 0.2);
        z-index: 1;
      }
      
      .arrow-3d {
        position: relative;
        z-index: 2;
        font-size: 24px;
        color: #00f0ff;
        text-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
        transition: all 0.3s ease;
      }
      
      &:hover::before {
        background: linear-gradient(135deg, 
          rgba(0, 60, 100, 0.9), 
          rgba(0, 140, 200, 0.7)
        );
        border-color: rgba(0, 240, 255, 0.9);
        box-shadow: 0 0 20px rgba(0, 0, 0, 0.4), 
                   inset 0 0 15px rgba(0, 240, 255, 0.4);
      }
      
      &:hover .arrow-3d {
        transform: scale(1.2);
        text-shadow: 0 0 15px rgba(0, 240, 255, 1);
      }
      
      &.prev {
        margin-right: 10px;
      }
      
      &.next {
        margin-left: 10px;
        
        &::before {
          transform: skewX(10deg);
        }
      }
      
      &:active .arrow-3d {
        transform: scale(0.9);
      }
    }
  }

  .robot-selector {
    flex: 1;
    height: 80px;
    display: flex;
    align-items: center;
    position: relative;
    overflow: hidden;
    border-radius: 8px;
    
    .selector-bg {
      position: absolute;
      inset: 0;
      background: linear-gradient(90deg, 
        rgba(0, 21, 41, 0.95), 
        rgba(0, 40, 80, 0.95) 20%, 
        rgba(0, 60, 100, 0.95) 50%, 
        rgba(0, 40, 80, 0.95) 80%, 
        rgba(0, 21, 41, 0.95)
      );
      box-shadow: inset 0 0 20px rgba(0, 240, 255, 0.2),
                  0 0 15px rgba(0, 0, 0, 0.5);
      border: 1px solid rgba(0, 240, 255, 0.3);
      z-index: 0;
    }
    
    &::before, &::after {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      width: 60px;
      z-index: 3;
      pointer-events: none;
    }
    
    &::before {
      left: 0;
      background: linear-gradient(90deg, rgba(0, 21, 41, 0.9), transparent);
    }
    
    &::after {
      right: 0;
      background: linear-gradient(-90deg, rgba(0, 21, 41, 0.9), transparent);
    }
    
    .selector-inner {
      display: flex;
      align-items: center;
      padding: 10px 15px;
      overflow-x: scroll;
      width: ~"calc(100vw - 175px)";
      scroll-behavior: smooth;
      scrollbar-width: none; /* Firefox */
      -ms-overflow-style: none; /* IE/Edge */
      position: relative;
      z-index: 2;
      
      &::-webkit-scrollbar {
        display: none; /* Chrome/Safari/Opera */
      }
    }
  }
  
  .robot-item {
    position: relative;
    flex: 0 0 auto;
    min-width: 160px;
    width: auto;
    height: 60px;
    margin: 0 10px;
    cursor: pointer;
    transition: all 0.3s ease;
    perspective: 400px;
    
    .hexagon-bg {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      clip-path: polygon(5% 0%, 95% 0%, 100% 50%, 95% 100%, 5% 100%, 0% 50%);
      background: linear-gradient(135deg, 
        rgba(0, 21, 41, 0.95), 
        rgba(0, 60, 100, 0.7)
      );
      border: 1px solid rgba(0, 240, 255, 0.4);
      box-shadow: 0 0 10px rgba(0, 0, 0, 0.5), inset 0 0 8px rgba(0, 240, 255, 0.15);
      transition: all 0.3s ease;
      z-index: 1;
    }
    
    .robot-content {
      position: relative;
      z-index: 2;
      display: flex;
      align-items: center;
      height: 100%;
      padding: 0 20px;
      white-space: nowrap;
    }
    
    &:hover {
      transform: translateY(-3px) scale(1.05);
      
      .hexagon-bg {
        background: linear-gradient(135deg, 
          rgba(0, 30, 60, 0.95), 
          rgba(0, 80, 120, 0.7)
        );
        border-color: rgba(0, 240, 255, 0.6);
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.6), inset 0 0 10px rgba(0, 240, 255, 0.25);
      }
    }
    
    &.active {
      transform: translateZ(10px) scale(1.05);
      
      .hexagon-bg {
        background: linear-gradient(135deg, 
          rgba(0, 40, 80, 0.95), 
          rgba(0, 100, 160, 0.8)
        );
        border-color: rgba(0, 240, 255, 0.9);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.7), inset 0 0 15px rgba(0, 240, 255, 0.4);
      }
      
      .robot-avatar .robot-icon {
        filter: drop-shadow(0 0 5px rgba(0, 240, 255, 0.8));
        animation: float 4s ease-in-out infinite;
      }
      
      .avatar-ring {
        opacity: 1;
        animation: float 4s ease-in-out infinite reverse;
      }
    }
      
    &.online {
      .robot-avatar .status-dot {
        background: #39FF14;
        box-shadow: 0 0 10px rgba(57, 255, 20, 0.8);
      }
      
      .signal-bars {
        .bar {
          background: #39FF14;
          &:nth-child(1) { height: 5px; }
          &:nth-child(2) { height: 10px; }
          &:nth-child(3) { height: 15px; }
        }
      }
    }
    
    &.offline {
      .robot-avatar .status-dot {
        background: #FF2D2D;
        box-shadow: 0 0 10px rgba(255, 45, 45, 0.8);
      }
      
      .signal-bars {
        .bar {
          background: #FF2D2D;
          opacity: 0.5;
          &:nth-child(1) { height: 5px; }
          &:nth-child(2) { height: 5px; }
          &:nth-child(3) { height: 5px; }
        }
      }
    }
    
    .robot-avatar {
      position: relative;
      width: 36px;
      height: 36px;
      border-radius: 6px;
      background: transparent;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 12px;
      
      .avatar-ring {
        position: absolute;
        top: -4px;
        left: -4px;
        width: ~"calc(100% + 8px)";
        height: ~"calc(100% + 8px)";
        border-radius: 8px;
        opacity: 0.5;
        transition: all 0.3s ease;
      }
      
      .robot-icon {
        width: 100%;
        height: 100%;
        object-fit: contain;
        z-index: 2;
        transition: all 0.3s ease;
      }
      
      .status-dot {
        position: absolute;
        bottom: -3px;
        right: -3px;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        animation: pulse 2s infinite;
        z-index: 3;
      }
    }
    
    .robot-info {
      flex: 0 0 auto;
      
      .robot-name {
        color: #fff;
        font-size: 14px;
        font-weight: 500;
        display: block;
        margin-bottom: 4px;
        white-space: nowrap;
      }
      
      .status-line {
        display: flex;
        align-items: center;
        gap: 12px;
        
        .robot-status {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.7);
          white-space: nowrap;
        }
        
        .signal-bars {
          display: flex;
          align-items: flex-end;
          gap: 2px;
          height: 15px;
          
          .bar {
            width: 4px;
            border-radius: 1px;
            transition: all 0.3s ease;
          }
        }
      }
    }
  }
  
  .header-right {
    display: flex;
    align-items: center;
    gap: 15px;
    
    .current-robot {
      position: relative;
      padding: 3px;
      border-radius: 8px;
      background: rgba(0, 21, 41, 0.8);
      border: 1px solid rgba(0, 240, 255, 0.5);
      text-align: center;
      overflow: hidden;
      
      .hologram-effect {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: repeating-linear-gradient(
          0deg,
          rgba(0, 240, 255, 0.05) 0px,
          rgba(0, 240, 255, 0) 1px,
          rgba(0, 240, 255, 0.05) 2px
        );
        pointer-events: none;
        z-index: 1;
      }
      
      .robot-id {
        font-family: 'Courier New', monospace;
        font-size: 12px;
        color: #00f0ff;
      }
      
      .name-status {
        position: relative;
        z-index: 2;
        
        .value {
          display: block;
          color: #fff;
          font-size: 16px;
          font-weight: 500;
          margin-bottom: 5px;
        }
        
        .status-badge {
          display: inline-block;
          padding: 2px 10px;
          border-radius: 10px;
          font-size: 12px;
          margin-bottom: 5px;
          
          &.online {
            background: rgba(57, 255, 20, 0.2);
            color: #39FF14;
            text-shadow: 0 0 5px rgba(57, 255, 20, 0.8);
          }
          
          &.offline {
            background: rgba(255, 45, 45, 0.2);
            color: #FF2D2D;
            text-shadow: 0 0 5px rgba(255, 45, 45, 0.8);
          }
        }
      }
      // 模式指示器
      .mode-indicator {
        position: relative;
        z-index: 2;
        display: flex;
        flex-direction: column;
        padding: 2px;
        border-radius: 5px;
        transition: all 0.3s ease;
        
        
        .indicator-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          
          i {
            font-size: 16px;
            filter: drop-shadow(0 0 3px currentColor);
          }
          
          .mode-text {
            font-size: 14px;
            font-weight: 700;
            letter-spacing: 1px;
            text-shadow: 0 0 8px currentColor;
          }
        }
        
        .indicator-detail {
          padding-left: 24px;
          
          .detail-text {
            font-size: 11px;
            opacity: 0.75;
            font-weight: 400;
            letter-spacing: 0.5px;
            line-height: 1.4;
          }
        }
        
        // 按 dataSource 区分颜色：实时为蓝色，历史为橙色
        
        // 1. 实时数据源 - 蓝色系
        &.mode-realtime {
          background: linear-gradient(135deg, 
            rgba(0, 191, 255, 0.4) 0%, 
            rgba(30, 144, 255, 0.35) 50%,
            rgba(0, 191, 255, 0.3) 100%
          );
          border: 1px solid rgba(0, 191, 255, 0.7);
          color: #00BFFF;
          box-shadow: 0 0 18px rgba(0, 191, 255, 0.6), inset 0 0 15px rgba(30, 144, 255, 0.2);
          animation: realtimePulse 2s ease-in-out infinite;
          
          i {
            animation: blink 1.5s ease-in-out infinite;
          }
          
          .indicator-detail .detail-text {
            color: rgba(0, 191, 255, 0.9);
          }
        }
        
        // 2. 历史数据源 - 橙色系
        &.mode-history {
          background: linear-gradient(135deg, 
            rgba(255, 152, 0, 0.4) 0%,
            rgba(255, 193, 7, 0.35) 50%,
            rgba(255, 152, 0, 0.3) 100%
          );
          border: 1px solid rgba(255, 152, 0, 0.7);
          color: #FF9800;
          box-shadow: 0 0 18px rgba(255, 152, 0, 0.6), inset 0 0 15px rgba(255, 193, 7, 0.2);
          
          .indicator-detail .detail-text {
            color: rgba(255, 152, 0, 0.9);
          }
        }
      }
  }
  
  // Tab 导航样式
  .tab-navigation {
    display: flex;
    flex-direction: column;
    gap: 8px;
    
    .primary-tabs {
      display: flex;
      gap: 8px;
      
      .primary-tab {
        position: relative;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 20px;
        background: rgba(0, 30, 60, 0.6);
        border: 2px solid rgba(0, 240, 255, 0.3);
        border-radius: 8px 8px 0 0;
        color: rgba(255, 255, 255, 0.7);
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        overflow: hidden;
        
        i {
          font-size: 18px;
        }
        
        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 3px;
          background: transparent;
          transition: all 0.3s ease;
        }
        
        &:hover:not(.disabled) {
          background: rgba(0, 50, 80, 0.8);
          border-color: rgba(0, 240, 255, 0.6);
          color: #00f0ff;
          transform: translateY(-2px);
        }
        
        &.active {
          background: linear-gradient(135deg, rgba(0, 50, 100, 0.9), rgba(0, 80, 140, 0.7));
          border-color: #00f0ff;
          border-bottom-color: transparent;
          color: #00f0ff;
          box-shadow: 0 -3px 15px rgba(0, 240, 255, 0.3);
          
          &::before {
            background: linear-gradient(90deg, #00f0ff, #00d4ff, #00f0ff);
            box-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
          }
          
          i {
            animation: iconPulse 2s ease-in-out infinite;
          }
        }
        
        // 历史查询标签激活时使用橙色主题
        &.history-tab.active {
          background: linear-gradient(135deg, rgba(255, 152, 0, 0.25), rgba(255, 193, 7, 0.2));
          border-color: #FF9800;
          color: #FF9800;
          box-shadow: 0 -3px 15px rgba(255, 152, 0, 0.4);
          
          &::before {
            background: linear-gradient(90deg, #FF9800, #FFA726, #FF9800);
            box-shadow: 0 0 10px rgba(255, 152, 0, 0.8);
          }
        }
        
        &.disabled {
          opacity: 0.4;
          cursor: not-allowed;
          background: rgba(0, 20, 40, 0.4);
          border-color: rgba(0, 240, 255, 0.15);
          
          &:hover {
            transform: none;
          }
        }
      }
    }
    
    .secondary-tabs {
      display: flex;
      gap: 4px;
      padding: 8px 12px;
      background: linear-gradient(135deg, rgba(0, 40, 80, 0.8), rgba(0, 21, 41, 0.9));
      border: 1px solid rgba(0, 240, 255, 0.3);
      border-radius: 0 0 8px 8px;
      border-top: none;
      
      .secondary-tab {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 8px 16px;
        background: rgba(0, 30, 60, 0.5);
        border: 1px solid rgba(0, 240, 255, 0.2);
        border-radius: 6px;
        color: rgba(255, 255, 255, 0.6);
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.25s ease;
        
        i {
          font-size: 16px;
        }
        
        &:hover {
          background: rgba(0, 50, 80, 0.7);
          border-color: rgba(0, 240, 255, 0.4);
          color: rgba(255, 255, 255, 0.9);
          transform: translateY(-1px);
        }
        
        &.active {
          background: linear-gradient(135deg, rgba(0, 240, 255, 0.25), rgba(0, 180, 255, 0.2));
          border-color: #00f0ff;
          color: #00f0ff;
          box-shadow: 0 0 12px rgba(0, 240, 255, 0.4), inset 0 0 8px rgba(0, 240, 255, 0.1);
          font-weight: 600;
          
          i {
            filter: drop-shadow(0 0 5px rgba(0, 240, 255, 0.8));
          }
        }
      }
    }
    
    // 历史查询模式下的子标签使用橙色主题
    &.history-mode {
      .secondary-tabs {
        .secondary-tab {
          &.active {
            background: linear-gradient(135deg, rgba(255, 152, 0, 0.25), rgba(255, 193, 7, 0.2));
            border-color: #FF9800;
            color: #FF9800;
            box-shadow: 0 0 12px rgba(255, 152, 0, 0.4), inset 0 0 8px rgba(255, 152, 0, 0.1);
            
            i {
              filter: drop-shadow(0 0 5px rgba(255, 152, 0, 0.8));
            }
          }
        }
      }
    }
  }
  
  .view-switch-btn,
  .data-source-btn {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: transparent;
    border: 1px solid rgba(0, 240, 255, 0.5);
    border-radius: 6px;
    color: #00f0ff;
    cursor: pointer;
    overflow: hidden;
    transition: all 0.3s ease;
    white-space: nowrap;
    
    .btn-bg {
      position: absolute;
      inset: 0;
      background: linear-gradient(45deg, rgba(0, 240, 255, 0.1), rgba(0, 21, 41, 0.5));
      z-index: 0;
    }
    
    .btn-icon {
      position: relative;
      z-index: 1;
      font-size: 18px;
    }
    
    .btn-text {
      position: relative;
      z-index: 1;
      font-size: 14px;
      font-weight: 500;
    }
    
    &:hover {
      transform: translateY(-2px);
      border-color: rgba(0, 240, 255, 0.8);
      box-shadow: 0 0 20px rgba(0, 240, 255, 0.3);
      
      .btn-bg {
        background: linear-gradient(45deg, rgba(0, 240, 255, 0.2), rgba(0, 21, 41, 0.7));
      }
    }
    
    &:active {
      transform: translateY(0);
    }
    
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
      border-color: rgba(0, 240, 255, 0.2);
      
      &:hover {
        transform: none;
        box-shadow: none;
      }
    }
  }
  
  .data-source-btn {
    border-color: rgba(57, 255, 20, 0.5);
    color: #39FF14;
    
    .btn-bg {
      background: linear-gradient(45deg, rgba(57, 255, 20, 0.1), rgba(0, 21, 41, 0.5));
    }
    
    &:hover {
      border-color: rgba(57, 255, 20, 0.8);
      box-shadow: 0 0 20px rgba(57, 255, 20, 0.3);
      
      .btn-bg {
        background: linear-gradient(45deg, rgba(57, 255, 20, 0.2), rgba(0, 21, 41, 0.7));
      }
    }
  }
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.1); opacity: 0.4; }
  100% { transform: scale(1); opacity: 0.8; }
}

@keyframes scan {
  0%, 20% { transform: translateY(-100%); }
  80%, 100% { transform: translateY(100%); }
}

@keyframes float {
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-5px);
  }
  100% {
    transform: translateY(0px);
  }
}

@keyframes realtimePulse {
  0%, 100% {
    box-shadow: 0 0 8px rgba(57, 255, 20, 0.2);
  }
  50% {
    box-shadow: 0 0 15px rgba(57, 255, 20, 0.5);
  }
}

@keyframes blink {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

@keyframes iconPulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.15);
  }
}

@keyframes slideIn {
  from {
    width: 0;
    opacity: 0;
  }
  to {
    width: 200px;
    opacity: 1;
  }
}
</style>
